import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Search, Copy, TriangleAlert, RefreshCw, Clock } from 'lucide-react';
import { getBroadcastGroupDetails, getBroadcastGroupExecutions } from '@/services/api/broadcast';
import type { BroadcastExecution } from '@/types/broadcast';
import { getStatusColor, getStatusLabel, getBroadcastTypeInfo } from '../utils/broadcastUtils';

interface ViewBroadcastGroupModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    groupBroadcastId: number | null;
}

const formatDate = (d?: string | null) => {
    if (!d) return '–';
    return new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

/**
 * Formata duração entre dois timestamps (ISO).
 * Exibe "—" se algum lado for nulo.
 * Exemplos: "12s", "1m 32s", "2h 15m"
 */
const formatDuration = (start?: string | null, end?: string | null): string => {
    if (!start || !end) return '–';
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (!isFinite(ms) || ms < 0) return '–';
    const totalSec = Math.round(ms / 1000);
    if (totalSec < 60) return `${totalSec}s`;
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return mm > 0 ? `${h}h ${mm}m` : `${h}h`;
};

const rowStatusColor: Record<string, string> = {
    completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    sent:      'bg-emerald-100 text-emerald-700 border-emerald-200',
    failed:    'bg-red-100 text-red-700 border-red-200',
    pending:   'bg-amber-100 text-amber-700 border-amber-200',
    processing:'bg-blue-100 text-blue-700 border-blue-200',
    sending:   'bg-blue-100 text-blue-700 border-blue-200',
    cancelled:  'bg-gray-100 text-gray-700 border-gray-200',
    translating:'bg-purple-100 text-purple-700 border-purple-200',
};

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
    return (
        <button
            role="switch"
            aria-checked={checked}
            onClick={() => onChange(!checked)}
            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${checked ? 'bg-blue-600' : 'bg-gray-200'}`}
        >
            <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform duration-200 ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
        </button>
    );
}

function KpiCard({ label, value, color }: { label: string; value: string | number; color: string }) {
    return (
        <div className="flex-1 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-center min-w-0">
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mt-0.5">{label}</p>
        </div>
    );
}

export function ViewBroadcastGroupModal({ open, onOpenChange, groupBroadcastId }: ViewBroadcastGroupModalProps) {
    const [execPage, setExecPage] = useState(1);
    const [execPerPage, setExecPerPage] = useState(15);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [onlyFlags, setOnlyFlags] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);

    useEffect(() => {
        if (!open) { setExecPage(1); setSearch(''); setDebouncedSearch(''); setOnlyFlags(false); setAutoRefresh(true); }
    }, [open]);
    useEffect(() => { setExecPage(1); setSearch(''); setDebouncedSearch(''); setOnlyFlags(false); setAutoRefresh(true); }, [groupBroadcastId]);

    // Debounce do search → 300ms
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    // Volta pra página 1 quando o termo de busca muda
    useEffect(() => { setExecPage(1); }, [debouncedSearch]);


    // Auto-refresh para a 0 quando broadcast chega em estado terminal
    const isTerminalStatus = (s?: string) => !!s && ['sent', 'completed', 'failed', 'cancelled'].includes(s);

    const { data: details, isLoading: loadingDetails, isFetching: fetchingDetails, refetch: refetchDetails } = useQuery({
        queryKey: ['broadcast-group-details', groupBroadcastId],
        queryFn: () => getBroadcastGroupDetails(groupBroadcastId!),
        enabled: open && groupBroadcastId !== null,
        staleTime: autoRefresh ? 0 : Infinity,
        refetchOnWindowFocus: false,
        refetchInterval: (q) => (autoRefresh && !isTerminalStatus(q.state.data?.status) ? 5000 : false),
    });

    const { data: executions, isLoading: loadingExec, isFetching: fetchingExec, refetch: refetchExec } = useQuery({
        queryKey: ['broadcast-group-executions', groupBroadcastId, execPage, execPerPage, debouncedSearch],
        queryFn: () => getBroadcastGroupExecutions(groupBroadcastId!, execPage, execPerPage, debouncedSearch || undefined),
        enabled: open && groupBroadcastId !== null,
        staleTime: autoRefresh ? 0 : Infinity,
        refetchOnWindowFocus: false,
        refetchInterval: autoRefresh && !isTerminalStatus(details?.status) ? 5000 : false,
        placeholderData: (prev) => prev,
    });

    const handleManualRefresh = () => {
        refetchDetails();
        refetchExec();
    };
    const isRefreshing = fetchingDetails || fetchingExec;

    const allItems: BroadcastExecution[] = executions?.items ?? [];
    const filteredExec = useMemo(() => {
        if (!onlyFlags) return allItems;
        return allItems.filter(e => e.token_status !== 'ok' || !!e.errors);
    }, [allItems, onlyFlags]);

    if (!open) return null;

    const groupName = details?.title || `Grupo #${groupBroadcastId}`;
    const status = details?.status || 'pending';


    return (
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50" onClick={() => onOpenChange(false)} />

            <div className="absolute inset-0 flex items-center justify-center p-2 sm:p-4 pointer-events-none">
                <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden pointer-events-auto flex flex-col">

                    {/* Header */}
                    <div className="px-6 pt-5 pb-4 border-b border-gray-100 shrink-0">
                        <div className="flex items-start justify-between pr-8 mb-3">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h2 className="text-lg font-bold text-gray-900 truncate max-w-xl">{groupName}</h2>
                                <Badge className={`${getStatusColor(status, false)} border rounded-full text-xs px-2.5 py-0.5 font-medium`}>
                                    {getStatusLabel(status)}
                                </Badge>
                            </div>
                            <button
                                onClick={() => onOpenChange(false)}
                                className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X className="w-5 h-5" strokeWidth={1.5} />
                            </button>
                        </div>

                        {/* Metadata */}
                        <div className="flex items-center gap-5 text-sm flex-wrap">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">ID</span>
                                <div className="flex items-center gap-1">
                                    <span className="font-mono text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded px-2 py-0.5">{groupBroadcastId}</span>
                                    <button onClick={() => navigator.clipboard.writeText(String(groupBroadcastId))} className="p-0.5 rounded hover:bg-gray-100 text-gray-400">
                                        <Copy className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                            {(() => {
                                const typeInfo = getBroadcastTypeInfo(details?.message_tag);
                                if (typeInfo.kind === 'standard') return null;
                                return (
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Tipo de Envio</span>
                                        <span className="text-xs font-medium text-gray-700">{typeInfo.label}</span>
                                    </div>
                                );
                            })()}
                            {details?.scheduled_at && (
                                <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Agendado para</span>
                                    <span className="text-xs font-medium text-gray-700">{formatDate(details.scheduled_at)}</span>
                                </div>
                            )}
                            {details?.created_at && (
                                <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Criado em</span>
                                    <span className="text-xs font-medium text-gray-700">{formatDate(details.created_at)}</span>
                                </div>
                            )}
                            {details?.started_at && details?.completed_at && (
                                <div className="flex items-center gap-1.5">
                                    <Clock className="h-3 w-3 text-gray-400" strokeWidth={2} />
                                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Tempo total</span>
                                    <span
                                        className="text-xs font-medium text-gray-700"
                                        title={`${formatDate(details.started_at)} → ${formatDate(details.completed_at)}`}
                                    >
                                        {formatDuration(details.started_at, details.completed_at)}
                                    </span>
                                </div>
                            )}
                            {details?.user_name && (
                                <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Por</span>
                                    <span className="text-xs font-medium text-gray-700">{details.user_name}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {loadingDetails ? (
                        <div className="flex items-center justify-center py-20">
                            <div className="animate-spin rounded-full h-7 w-7 border-2 border-gray-200 border-t-gray-800" />
                        </div>
                    ) : details ? (
                        <div className="flex flex-col flex-1 overflow-hidden">

                            {/* KPIs */}
                            <div className="px-6 py-4 border-b border-gray-100 shrink-0">
                                <div className="flex gap-3">
                                    <KpiCard label="Págs Total" value={details.total_pages} color="text-gray-900" />
                                    <KpiCard label="Págs Enviadas" value={details.pages_sent} color="text-emerald-600" />
                                    <KpiCard label="Págs Falhas" value={details.pages_failed} color="text-red-500" />
                                    <KpiCard label="Processando" value={details.pages_processing ?? (details.total_pages - details.pages_sent - details.pages_failed)} color="text-cyan-600" />
                                    <KpiCard label="Leads Enviados" value={details.total_sent.toLocaleString('pt-BR')} color="text-blue-600" />
                                    <KpiCard label="Leads Entregues" value={details.total_delivered != null ? details.total_delivered.toLocaleString('pt-BR') : '–'} color="text-indigo-600" />
                                </div>
                            </div>

                            {/* Toolbar */}
                            <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between shrink-0">
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2">
                                        <Toggle checked={onlyFlags} onChange={setOnlyFlags} />
                                        <span className="text-sm text-gray-600">Apenas com flags</span>
                                    </div>
                                    <div className="h-5 w-px bg-gray-200" />
                                    <button
                                        type="button"
                                        onClick={handleManualRefresh}
                                        disabled={isRefreshing}
                                        title="Atualizar agora"
                                        className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 transition-colors"
                                    >
                                        <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} strokeWidth={2} />
                                    </button>
                                    <div className="flex items-center gap-2">
                                        <Toggle checked={autoRefresh} onChange={setAutoRefresh} />
                                        <span className="text-sm text-gray-600">Atualizar a cada 5s</span>
                                        {autoRefresh && !isTerminalStatus(details?.status) && (
                                            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                        )}
                                    </div>
                                </div>
                                <div className="relative w-64">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <Input
                                        placeholder="Buscar por nome ou ID..."
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        className="pl-9 h-8 text-sm"
                                    />
                                    {fetchingExec && search && (
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 rounded-full border-2 border-gray-200 border-t-gray-500 animate-spin" />
                                    )}
                                </div>
                            </div>

                            {/* Table */}
                            <div className="flex-1 overflow-auto">
                                {loadingExec ? (
                                    <div className="flex items-center justify-center py-16">
                                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-gray-800" />
                                    </div>
                                ) : (
                                    <table className="w-full text-sm table-fixed">
                                        <colgroup>
                                            <col style={{ width: '32%' }} />
                                            <col style={{ width: '14%' }} />
                                            <col style={{ width: '14%' }} />
                                            <col style={{ width: '14%' }} />
                                            <col style={{ width: '14%' }} />
                                            <col style={{ width: '12%' }} />
                                        </colgroup>
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr className="border-b border-gray-100">
                                                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Página</th>
                                                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                                <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                                                <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Enviadas</th>
                                                <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Entregues</th>
                                                <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Tempo</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {filteredExec.map((e: BroadcastExecution) => {
                                                const hasFlag = e.token_status !== 'ok' || !!e.errors;
                                                const deliveredPct = e.total_sent > 0 && e.total_delivered != null
                                                    ? (e.total_delivered / e.total_sent) * 100
                                                    : null;
                                                const pctColor = deliveredPct === null ? '' : deliveredPct >= 100 ? 'text-emerald-500' : 'text-amber-500';
                                                const pctArrow = deliveredPct !== null && deliveredPct < 100 ? '↓' : '✓';
                                                return (
                                                    <tr key={e.broadcast_id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3">
                                                            <div className="flex items-start gap-1.5">
                                                                {hasFlag && <TriangleAlert className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />}
                                                                <div className="min-w-0">
                                                                    <p className="font-medium text-gray-900 truncate">{e.page_name}</p>
                                                                    <p className="text-[11px] text-gray-400 font-mono">{e.page_id}</p>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <Badge className={`${rowStatusColor[e.status] || 'bg-gray-100 text-gray-700 border-gray-200'} border text-xs whitespace-nowrap`}>
                                                                {getStatusLabel(e.status)}
                                                            </Badge>
                                                        </td>
                                                        <td className="px-4 py-3 text-center text-gray-700">{e.total_leads.toLocaleString('pt-BR')}</td>
                                                        <td className="px-4 py-3 text-center text-blue-600 font-semibold">{e.total_sent.toLocaleString('pt-BR')}</td>
                                                        <td className="px-4 py-3 text-center">
                                                            {e.total_delivered != null ? (
                                                                <>
                                                                    <p className="font-semibold text-blue-600">{e.total_delivered.toLocaleString('pt-BR')}</p>
                                                                    {deliveredPct !== null && (
                                                                        <p className={`text-[11px] font-medium ${pctColor}`}>{pctArrow} {deliveredPct.toFixed(1)}%</p>
                                                                    )}
                                                                </>
                                                            ) : '–'}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span
                                                                className="text-xs font-medium text-gray-600 font-mono"
                                                                title={
                                                                    e.started_at && e.completed_at
                                                                        ? `${formatDate(e.started_at)} → ${formatDate(e.completed_at)}`
                                                                        : 'Sem tempos registrados'
                                                                }
                                                            >
                                                                {formatDuration(e.started_at, e.completed_at)}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {filteredExec.length === 0 && !loadingExec && (
                                                <tr>
                                                    <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-400">
                                                        {debouncedSearch
                                                            ? `Nenhuma execução encontrada para "${debouncedSearch}".`
                                                            : onlyFlags
                                                                ? 'Nenhuma execução com flags.'
                                                                : 'Nenhuma execução encontrada.'}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            {/* Pagination */}
                            <div className="flex items-center justify-between gap-3 py-3 px-4 border-t border-gray-100 shrink-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-400">Por página:</span>
                                    <select
                                        value={execPerPage}
                                        onChange={e => { setExecPerPage(Number(e.target.value)); setExecPage(1); }}
                                        className="text-sm border border-gray-200 rounded-lg px-2 py-1 text-gray-700 bg-white focus:outline-none"
                                    >
                                        {[15, 30, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                                <span className="text-sm text-gray-400">
                                    Página {execPage} de {executions?.pages ?? 1} com {executions?.total ?? 0} execuções
                                </span>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" className="rounded-xl" disabled={execPage <= 1} onClick={() => setExecPage(p => p - 1)}>Anterior</Button>
                                    <Button variant="outline" size="sm" className="rounded-xl" disabled={execPage >= (executions?.pages ?? 1)} onClick={() => setExecPage(p => p + 1)}>Próxima</Button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
